import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { operations } from '@luxuryescapes/contract-svc-promo/generated';

import OrderSearchForm, { MultiFilterState } from '~/components/Common/Forms/OrderSearchForm';
import Spinner from '~/components/Common/Spinner';
import DebugModal from '~/components/DebugModal/DebugModal';

import { getOrdersByIds, getPurchases, multiSearch, searchPurchases } from '~/services/OrdersService';
import { debugPromoByOrderID } from '~/services/PromoService';

import currencyFormatter from '~/utils/currencyFormatter';
import { getVoucherIdsFromPromoPaymentResponse } from '~/utils/getVoucherIdsFromPromoPaymentResponse';

type Source = 'order-ids' | 'page' | 'random';
type CalculatorStatus = 'pending' | 'running' | 'complete' | 'error';

type OrderRecord = Record<string, unknown>;
type DiscountCalculationResult =
  operations['calculateDiscountByOrderId']['responses']['200']['content']['application/json']['result'];

type SimulationRow = {
  id: string;
  orderId: string;
  orderNumber: string;
  createdAt: string;
  total: string;
  status: string;
  calculatorStatus: CalculatorStatus;
  promoCode: string;
  discount: string;
  vouchers: string;
  error: string;
  debugData?: unknown;
};

type SearchDefinition = {
  filterField: string;
  filterValue: string;
  additionalFilter?: MultiFilterState;
  businessId?: string | number;
};

const MAX_ORDERS = 25;
const RANDOM_PAGE_SEARCHES = 3;
const REQUEST_GAP_MS = 350;

function stringValue(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value).trim() : '';
}

function isOrderRecord(value: unknown): value is OrderRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function orderRecords(value: unknown): Array<OrderRecord> {
  return Array.isArray(value) ? value.filter(isOrderRecord) : [];
}

function orderIdFor(order: OrderRecord): string {
  return stringValue(order.id_orders ?? order.id);
}

function orderNumberFor(order: OrderRecord): string {
  return stringValue(order.order_number ?? order.orderNumber ?? order.booking_number);
}

function formatOrderTotal(order: OrderRecord): string {
  const total = Number(order.total ?? order.total_amount ?? order.totalAmount ?? order.grand_total);
  const currency = stringValue(order.currency_code ?? order.currencyCode ?? order.currency) || 'AUD';
  return Number.isFinite(total) ? currencyFormatter(currency, total) : '-';
}

function makeRow(order: OrderRecord): SimulationRow {
  const orderId = orderIdFor(order);
  return {
    id: orderId,
    orderId,
    orderNumber: orderNumberFor(order),
    createdAt: stringValue(order.created_at ?? order.createdAt),
    total: formatOrderTotal(order),
    status: stringValue(order.status ?? order.order_status),
    calculatorStatus: 'pending',
    promoCode: '',
    discount: '',
    vouchers: '',
    error: '',
    debugData: order,
  };
}

function tokenizeIds(value: string): Array<string> {
  return Array.from(
    new Set(
      value
        .split(/[\s,]+/)
        .map((id) => id.trim())
        .filter(Boolean),
    ),
  );
}

function parseOrderIds(value: string): Array<string> {
  return tokenizeIds(value).slice(0, MAX_ORDERS);
}

function getOrderVoucherIds(order: OrderRecord): Array<string> {
  const payments = Array.isArray(order.payments) ? order.payments : [];
  return Array.from(
    new Set(
      payments.flatMap((payment) => {
        if (!payment || typeof payment !== 'object' || Array.isArray(payment)) {
          return [];
        }
        const responseJson = (payment as { response_json?: unknown }).response_json;
        return getVoucherIdsFromPromoPaymentResponse(
          responseJson && typeof responseJson === 'object' && !Array.isArray(responseJson)
            ? (responseJson as Record<string, unknown>)
            : undefined,
        );
      }),
    ),
  );
}

function getOrderPromoCode(order: OrderRecord): string {
  const orderCode = stringValue(order.promo_code ?? order.promoCode ?? order.code_name);
  if (orderCode) {
    return orderCode;
  }
  const payments = Array.isArray(order.payments) ? order.payments : [];
  for (const payment of payments) {
    if (!payment || typeof payment !== 'object' || Array.isArray(payment)) {
      continue;
    }
    const responseJson = (payment as { response_json?: unknown }).response_json;
    if (!responseJson || typeof responseJson !== 'object' || Array.isArray(responseJson)) {
      continue;
    }
    const details = (responseJson as { details?: unknown }).details;
    const code = stringValue(
      (responseJson as Record<string, unknown>).promoCode ??
        (responseJson as Record<string, unknown>).promo_code ??
        (responseJson as Record<string, unknown>).code_name ??
        (details && typeof details === 'object' && !Array.isArray(details)
          ? (details as Record<string, unknown>).code_name
          : undefined),
    );
    if (code) {
      return code;
    }
  }
  return '';
}

function randomItems<T>(items: Array<T>, count: number): Array<T> {
  return [...items].sort(() => Math.random() - 0.5).slice(0, count);
}

function delay(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

export default function PromoSimulatorMulti({ tenant }: { tenant: App.Tenant }) {
  const [source, setSource] = useState<Source>('order-ids');
  const [orderIdsText, setOrderIdsText] = useState('');
  const [page, setPage] = useState(1);
  const [sampleSize, setSampleSize] = useState(10);
  const [searchDefinition, setSearchDefinition] = useState<SearchDefinition>();
  const [codeName, setCodeName] = useState('');
  const [voucherIdsText, setVoucherIdsText] = useState('');
  const [applyWithOriginalUserAccount, setApplyWithOriginalUserAccount] = useState(false);
  const [ignoreLuxPlusPricing, setIgnoreLuxPlusPricing] = useState(true);
  const [rows, setRows] = useState<Array<SimulationRow>>([]);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const runIdRef = useRef(0);
  const additionalFilterRef = useRef<MultiFilterState | undefined>(undefined);

  const updateRow = useCallback((orderId: string, update: Partial<SimulationRow>) => {
    setRows((currentRows) => currentRows.map((row) => (row.orderId === orderId ? { ...row, ...update } : row)));
  }, []);

  const fetchPage = useCallback(
    async (pageNumber: number, perPage: number): Promise<{ orders: Array<OrderRecord>; total: number }> => {
      if (!searchDefinition) {
        throw new Error('Run an order search before selecting orders.');
      }

      const { filterField, filterValue, additionalFilter, businessId } = searchDefinition;
      if (filterField === 'universal_search_id') {
        const result = await searchPurchases({ page: pageNumber - 1, perPage, searchId: filterValue });
        return { orders: orderRecords(result.result), total: Number(result.total ?? 0) };
      }
      if (filterField === 'multi_search') {
        const result = await multiSearch({
          page: pageNumber - 1,
          perPage,
          orderBy: 'created_at',
          orderDirection: 'desc',
          additionalFilter,
        });
        return { orders: orderRecords(result.result), total: Number(result.total ?? 0) };
      }

      const result = await getPurchases({
        page: pageNumber,
        per_page: perPage,
        brand: tenant.brand,
        filterBy: filterField,
        filterValue,
        additionalFilter,
        businessId,
      });
      return { orders: orderRecords(result.result), total: Number(result.total ?? 0) };
    },
    [searchDefinition, tenant.brand],
  );

  const simulateRows = useCallback(
    async (selectedRows: Array<SimulationRow>, runId: number) => {
      const normalizedCodeName = codeName.trim();
      const voucherOverrides = tokenizeIds(voucherIdsText);
      const hasOverride = Boolean(normalizedCodeName) || voucherOverrides.length > 0;
      for (const row of selectedRows) {
        if (runId !== runIdRef.current) {
          return;
        }
        updateRow(row.orderId, { calculatorStatus: 'running' });
        try {
          const order = row.debugData;
          const existingVoucherIds = isOrderRecord(order) ? getOrderVoucherIds(order) : [];
          const effectiveCodeName =
            hasOverride || !isOrderRecord(order) ? normalizedCodeName : getOrderPromoCode(order);
          const response = await debugPromoByOrderID(
            effectiveCodeName,
            row.orderId,
            tenant.brand,
            applyWithOriginalUserAccount,
            ignoreLuxPlusPricing,
            () => undefined,
            hasOverride ? voucherOverrides : existingVoucherIds,
          );
          if (response.status !== 200) {
            updateRow(row.orderId, { calculatorStatus: 'error', error: JSON.stringify(response), debugData: response });
          } else {
            const result: DiscountCalculationResult = response.result;
            const { promo } = result;
            const currency = promo.currency;
            const discount = promo.discount_total;
            const voucherTotal = promo.voucherTotal;
            updateRow(row.orderId, {
              calculatorStatus: 'complete',
              promoCode: promo.code_name || effectiveCodeName,
              discount: Number.isFinite(discount) ? currencyFormatter(currency, discount) : '-',
              vouchers: Number.isFinite(voucherTotal) ? currencyFormatter(currency, voucherTotal) : '-',
              debugData: response,
            });
          }
        } catch (error) {
          updateRow(row.orderId, {
            calculatorStatus: 'error',
            error: error instanceof Error ? error.message : JSON.stringify(error),
            debugData: error,
          });
        }
        await delay(REQUEST_GAP_MS);
      }
    },
    [applyWithOriginalUserAccount, codeName, ignoreLuxPlusPricing, tenant.brand, updateRow, voucherIdsText],
  );

  const selectAndSimulate = useCallback(async () => {
    setIsSelecting(true);
    setSelectionError(null);
    const runId = runIdRef.current + 1;
    runIdRef.current = runId;
    try {
      let selectedOrders: Array<OrderRecord>;
      const count = Math.max(1, Math.min(MAX_ORDERS, sampleSize));
      if (source === 'order-ids') {
        const ids = parseOrderIds(orderIdsText);
        if (ids.length === 0) {
          throw new Error('Enter at least one order ID.');
        }
        selectedOrders = orderRecords(await getOrdersByIds(ids, tenant.brand));
      } else if (source === 'page') {
        selectedOrders = (await fetchPage(Math.max(1, page), count)).orders;
      } else {
        const firstPage = await fetchPage(1, 1);
        const totalPages = Math.max(1, Math.ceil(firstPage.total / count));
        const pageNumbers = Array.from(
          new Set(Array.from({ length: RANDOM_PAGE_SEARCHES }, () => 1 + Math.floor(Math.random() * totalPages))),
        );
        const pageResults = await Promise.all(pageNumbers.map((pageNumber) => fetchPage(pageNumber, count)));
        selectedOrders = randomItems(
          pageResults.flatMap((result) => result.orders),
          count,
        );
      }

      const selectedRows = selectedOrders.map(makeRow).filter((row) => Boolean(row.orderId));
      if (selectedRows.length === 0) {
        throw new Error('No orders matched this selection.');
      }
      setRows(selectedRows);
      void simulateRows(selectedRows, runId);
    } catch (error) {
      setSelectionError(error instanceof Error ? error.message : JSON.stringify(error));
    } finally {
      setIsSelecting(false);
    }
  }, [fetchPage, orderIdsText, page, sampleSize, simulateRows, source, tenant.brand]);

  const columns = useMemo<Array<GridColDef<SimulationRow>>>(
    () => [
      { field: 'orderId', headerName: 'Order ID', minWidth: 150, flex: 1 },
      { field: 'orderNumber', headerName: 'Order #', minWidth: 120 },
      { field: 'createdAt', headerName: 'Created', minWidth: 165 },
      { field: 'status', headerName: 'Order status', minWidth: 120 },
      { field: 'total', headerName: 'Order total', minWidth: 120 },
      {
        field: 'calculatorStatus',
        headerName: 'Calculator',
        minWidth: 130,
        renderCell: ({ row }) =>
          row.calculatorStatus === 'running' ? <Spinner size={18} /> : <>{row.calculatorStatus}</>,
      },
      { field: 'promoCode', headerName: 'Promo', minWidth: 120 },
      { field: 'discount', headerName: 'Discount', minWidth: 120 },
      { field: 'vouchers', headerName: 'Vouchers', minWidth: 120 },
      {
        field: 'result',
        headerName: 'Result',
        minWidth: 220,
        flex: 1,
        renderCell: ({ row }) =>
          row.error ? (
            <Typography variant="body2" color="error" noWrap title={row.error}>
              {row.error}
            </Typography>
          ) : row.debugData ? (
            <DebugModal title="Details" type="generic" data={row.debugData} />
          ) : null,
      },
    ],
    [],
  );

  const handleOrderSearch = useCallback(
    (filterField: unknown, filterValue: unknown, params?: { businessId?: string | number }) => {
      setSearchDefinition({
        filterField: stringValue(filterField),
        filterValue: stringValue(filterValue),
        additionalFilter: additionalFilterRef.current,
        businessId: params?.businessId,
      });
    },
    [],
  );

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Select a compact set of orders, then evaluate the discount calculator serially. Requests are spaced to avoid
        placing unnecessary load on promo services.
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        <TextField
          label="Promo code"
          value={codeName}
          onChange={(event) => setCodeName(event.target.value)}
          fullWidth
        />
        <TextField
          select
          label="Order source"
          value={source}
          onChange={(event) => setSource(event.target.value as Source)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="order-ids">Entered order IDs</MenuItem>
          <MenuItem value="page">A search results page</MenuItem>
          <MenuItem value="random">Random search sample</MenuItem>
        </TextField>
        <TextField
          label="Orders to simulate"
          type="number"
          value={sampleSize}
          onChange={(event) => setSampleSize(Number(event.target.value))}
          inputProps={{ min: 1, max: MAX_ORDERS }}
          sx={{ minWidth: 150 }}
        />
      </Stack>
      <TextField
        label="Voucher IDs (optional)"
        value={voucherIdsText}
        onChange={(event) => setVoucherIdsText(event.target.value)}
        helperText="Optional override; otherwise vouchers already attached to each order are reapplied"
        fullWidth
      />
      <Stack direction="row" spacing={1}>
        <Button
          variant={applyWithOriginalUserAccount ? 'contained' : 'text'}
          onClick={() => setApplyWithOriginalUserAccount((value) => !value)}
        >
          Original user account
        </Button>
        <Button
          variant={ignoreLuxPlusPricing ? 'contained' : 'text'}
          onClick={() => setIgnoreLuxPlusPricing((value) => !value)}
        >
          Ignore LuxPlus pricing
        </Button>
      </Stack>

      {source === 'order-ids' ? (
        <TextField
          label="Order IDs"
          value={orderIdsText}
          onChange={(event) => setOrderIdsText(event.target.value)}
          multiline
          minRows={3}
          helperText={`Up to ${MAX_ORDERS} comma- or whitespace-separated IDs`}
          fullWidth
        />
      ) : (
        <>
          <Typography variant="subtitle2">Order filter</Typography>
          <OrderSearchForm
            searchQuery={handleOrderSearch}
            setAdditionalParams={(additionalFilter: MultiFilterState) => {
              additionalFilterRef.current = additionalFilter;
              setSearchDefinition((current) =>
                current
                  ? { ...current, additionalFilter }
                  : { filterField: 'created_at', filterValue: '', additionalFilter },
              );
            }}
            bookingNumberPlaceholder="Search orders or choose a filter"
            bookingIdType="universal_search_id"
            showMultiSearch
            showOrderNotesFilter
          />
          {source === 'page' && (
            <TextField
              label="Results page"
              type="number"
              value={page}
              onChange={(event) => setPage(Number(event.target.value))}
              inputProps={{ min: 1 }}
              sx={{ maxWidth: 180 }}
            />
          )}
          {!searchDefinition && (
            <Alert severity="info">Run the order filter above to set the search used for this sample.</Alert>
          )}
        </>
      )}

      {selectionError && <Alert severity="error">{selectionError}</Alert>}
      <Box>
        <Button variant="contained" onClick={selectAndSimulate} disabled={isSelecting}>
          {isSelecting ? 'Selecting orders…' : 'Select and simulate'}
        </Button>
      </Box>
      {rows.length > 0 && (
        <Box sx={{ height: 560 }}>
          <DataGrid rows={rows} columns={columns} disableRowSelectionOnClick getRowHeight={() => 'auto'} />
        </Box>
      )}
    </Stack>
  );
}
